import { Box, Typography } from '@mui/material';
import { AltLadder } from './AltLadder';
import {
  ALT_EXIT_PENDING_BODY_POST,
  ALT_EXIT_PENDING_BODY_PRE,
  ALT_EXIT_PENDING_BOLD,
  ALT_EXIT_PENDING_TITLE,
  altCompositeStatus,
  altDerivedComposites,
  altDomainStatuses,
  altGrowChipLabel,
  altLevelIndex,
} from './altPlanContent';
import { ALTERNATE_SCALE, altLevelLabel, DOMAIN_LABELS, type PlanStudent } from './derive';
import { CARD_SHADOW, COLORS, TREND_STYLES } from './tokens';

function trendOf(delta: number) {
  return delta > 0 ? TREND_STYLES.rising : delta < 0 ? TREND_STYLES.falling : TREND_STYLES.flat;
}

function scaleCaption(scale: number | null, priorScale: number | null, delta: number | null): string {
  if (scale == null) return 'No scale score';
  if (delta == null || priorScale == null) return `Scale ${scale}`;
  if (delta > 0) return `Scale ${scale} · up from ${priorScale}`;
  if (delta < 0) return `Scale ${scale} · down from ${priorScale}`;
  return `Scale ${scale} · even with the prior administration`;
}

function UpChip({ delta }: { delta: number | null }) {
  if (delta == null || delta <= 0) return null;
  return (
    <Box component="span" sx={{ fontSize: 12, fontWeight: 700, color: TREND_STYLES.rising.fg }}>
      {`▲ +${delta}`}
    </Box>
  );
}

export function AltProficiencyPanel({ student }: { student: PlanStudent }) {
  const overall = altCompositeStatus(student);
  const statuses = altDomainStatuses(student);
  const comps = altDerivedComposites(student);
  const first = student.firstName || 'This student';
  const grow = altGrowChipLabel(overall);
  const topIndex = Math.max(
    ...statuses.map((s) => (s.level != null ? altLevelIndex(s.level) : 0)),
  );
  const strongest = statuses
    .filter((s) => s.level != null && altLevelIndex(s.level) === topIndex)
    .map((s) => DOMAIN_LABELS[s.domain]);
  const summary = [
    overall.level != null
      ? `${first} is at Alternate ACCESS · Level ${altLevelIndex(overall.level)} — ${overall.label}${overall.scale != null ? ` (Overall scale ${overall.scale})` : ''}.`
      : `${first} has no scored overall Alternate ACCESS administration on file.`,
    strongest.length > 0 && strongest.length < 4
      ? `${strongest.join(' and ')} ${strongest.length === 1 ? 'is' : 'are'} strongest at Level ${topIndex} — ${altLevelLabel(topIndex)}.`
      : '',
    statuses.every((s) => s.delta != null && s.delta > 0)
      ? 'Every domain rose from the prior administration on this stable scale.'
      : '',
    'Exit standing is an IEP / case-conference team determination — see below.',
  ]
    .filter((part) => part !== '')
    .join(' ');
  return (
    <Box sx={{ bgcolor: '#FFFFFF', borderRadius: '12px', boxShadow: CARD_SHADOW, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 24, lineHeight: '32px', fontWeight: 600, color: COLORS.ink }}>
            Current Proficiency
          </Typography>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: '9999px', bgcolor: COLORS.bg, color: COLORS.navy, fontSize: 12, fontWeight: 700 }}>
            Alternate ACCESS scale
          </Box>
        </Box>
        <Typography sx={{ fontSize: 16, fontWeight: 500, color: COLORS.muted }}>
          {`WIDA Alternate ACCESS${overall.year != null ? `, Spring ${overall.year}` : ''} · Levels ${ALTERNATE_SCALE.levelMin}–${ALTERNATE_SCALE.levelMax} (${ALTERNATE_SCALE.scaleMin}–${ALTERNATE_SCALE.scaleMax}) — not equivalent to regular ACCESS levels`}
        </Typography>
      </Box>
      <Box sx={{ maxWidth: 760 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.ink }}>Proficiency summary</Typography>
        <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 500, color: COLORS.ink }}>
          {summary}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', flexWrap: 'wrap' }}>
        <Box sx={{ width: 300, flexShrink: 0, bgcolor: COLORS.bg, borderRadius: '12px', p: 2.5, display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: COLORS.navy }}>
            Alternate ACCESS · Overall
          </Typography>
          <Typography sx={{ fontSize: 40, lineHeight: '44px', fontWeight: 800, letterSpacing: '-0.5px', color: COLORS.ink }}>
            {overall.level != null ? `Level ${altLevelIndex(overall.level)}` : '—'}
          </Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: COLORS.ink }}>{overall.label}</Typography>
          <AltLadder level={overall.level} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 600, color: COLORS.muted }}>
            <span>1 Entering</span>
            <span>5 Bridging</span>
          </Box>
          {overall.scale != null && (
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.ink }}>
              {`Scale ${overall.scale} `}
              <Box component="span" sx={{ fontWeight: 500, color: COLORS.muted }}>
                {`(${ALTERNATE_SCALE.scaleMin}–${ALTERNATE_SCALE.scaleMax})`}
              </Box>
            </Typography>
          )}
          {grow != null && overall.delta != null && (
            <Box>
              <Box
                component="span"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 10px', borderRadius: '9999px', bgcolor: trendOf(overall.delta).bg, color: COLORS.ink, fontSize: 12, fontWeight: 700 }}
              >
                {overall.delta !== 0 && (
                  <Box component="span" sx={{ fontSize: 10, color: trendOf(overall.delta).fg }}>
                    {overall.delta > 0 ? '▲' : '▼'}
                  </Box>
                )}
                {overall.delta === 0 ? grow.replace(/^\+0/, 'No change') : grow}
              </Box>
            </Box>
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 280, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {statuses.map((s) => (
            <Box key={s.domain} sx={{ flex: '1 1 45%', minWidth: 160, bgcolor: COLORS.bg, borderRadius: '8px', p: 1.5, display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.muted }}>
                  {DOMAIN_LABELS[s.domain]}
                </Typography>
                <UpChip delta={s.delta} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                <Typography sx={{ fontSize: 24, lineHeight: '28px', fontWeight: 800, color: COLORS.ink }}>
                  {s.level != null ? `Level ${altLevelIndex(s.level)}` : '—'}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.ink }}>{s.label}</Typography>
              </Box>
              <AltLadder level={s.level} />
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: COLORS.muted }}>
                {scaleCaption(s.scale, s.priorScale, s.delta)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: COLORS.muted }}>
          Other composites
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {comps.map((c) => (
            <Box key={c.name} sx={{ flex: 1, minWidth: 160, bgcolor: COLORS.bg, borderRadius: '8px', p: 1.5, display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.ink }}>{c.name}</Typography>
                <UpChip delta={c.delta} />
              </Box>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.ink }}>
                {c.level != null ? `Level ${c.level} · ${c.label}` : '—'}
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: COLORS.muted }}>
                {c.scale != null ? `Scale ${c.scale} · ${c.parts}` : c.parts}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 2, borderRadius: '8px', bgcolor: COLORS.bg, outline: `1px solid ${COLORS.divider}`, outlineOffset: '-1px' }}>
        <Box sx={{ flexShrink: 0, width: 40, height: 40, borderRadius: '9999px', bgcolor: COLORS.navy, color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 800, fontStyle: 'italic' }}>
          i
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 700, color: COLORS.ink }}>
            {ALT_EXIT_PENDING_TITLE}
          </Typography>
          <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 500, color: COLORS.ink }}>
            {ALT_EXIT_PENDING_BODY_PRE}
            <Box component="b" sx={{ fontWeight: 700 }}>{ALT_EXIT_PENDING_BOLD}</Box>
            {ALT_EXIT_PENDING_BODY_POST}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
